import { type ReadonlyMat2d, type ReadonlyVec2, vec2 } from 'gl-matrix';

import {
  createCubicBezier,
  getPointOnCubicBezier,
  walkPointsAlongCubicBezier,
  type CubicBezier,
} from '@canvas/bezier';
import { transformCubicBezier } from '@canvas/bezier';

import { type GraphicColor, type GraphicColorDefinition } from './color.ts';
import { GraphicPoint, type ReadonlyGraphicPoint } from './points.ts';

/**
 * A segment of a graphic that can be rendered. It can be a line, cubic bezier curve,
 * or a sub-path start.
 */
export type GraphicSegment = GraphicSubPathStart | GraphicLine | GraphicCubicBezier;

/**
 * A segment of a graphic that represents the start of a sub-path. It indicates that the
 * next segment should be treated as a new sub-path, disconnected from the previous
 * segments.
 */
export class GraphicSubPathStart {
  constructor(public start: ReadonlyGraphicPoint) {}
}

/**
 * A segment of a graphic that represents a straight line.
 */
export class GraphicLine {
  constructor(
    public start: ReadonlyGraphicPoint,
    public end: ReadonlyGraphicPoint,
    public options: GraphicSegmentOptions | null = null,
  ) {}

  static fromPositionValuesOnly(sx: number, sy: number, ex: number, ey: number) {
    return new GraphicLine(
      GraphicPoint.set(GraphicPoint.create(), sx, sy, 0, 0),
      GraphicPoint.set(GraphicPoint.create(), ex, ey, 0, 0),
    );
  }
}

/**
 * A segment of a graphic that represents a cubic bezier curve.
 */
export class GraphicCubicBezier {
  private readonly posBezier: CubicBezier;
  private readonly offsetBezier: CubicBezier;

  private static readonly posFlyweight = vec2.create();
  private static readonly offsetFlyweight = vec2.create();

  constructor(
    public start: ReadonlyGraphicPoint,
    public control1: ReadonlyGraphicPoint,
    public control2: ReadonlyGraphicPoint,
    public end: ReadonlyGraphicPoint,
    public options: GraphicSegmentOptions | null = null,
  ) {
    this.posBezier = createCubicBezier(
      start.pos(vec2.create()),
      control1.pos(vec2.create()),
      control2.pos(vec2.create()),
      end.pos(vec2.create()),
    );

    this.offsetBezier = createCubicBezier(
      start.offset(vec2.create()),
      control1.offset(vec2.create()),
      control2.offset(vec2.create()),
      end.offset(vec2.create()),
    );
  }

  getPoint(out: GraphicPoint, t: number): GraphicPoint {
    return GraphicPoint.fromPositionsAndOffsets(
      out,
      getPointOnCubicBezier(GraphicCubicBezier.posFlyweight, this.posBezier, t),
      getPointOnCubicBezier(GraphicCubicBezier.offsetFlyweight, this.offsetBezier, t),
    );
  }

  walkPoints(
    amount: number,
    each: (p1: ReadonlyGraphicPoint, t: number, i: number) => boolean | void,
    startProportion = 0,
    endProportion = 1,
  ): void {
    const p = GraphicPoint.create();
    const offsetFlyweight = vec2.create();

    walkPointsAlongCubicBezier(
      this.posBezier,
      amount,
      (pos, t, i) =>
        each(
          GraphicPoint.fromPositionsAndOffsets(
            p,
            pos,
            getPointOnCubicBezier(offsetFlyweight, this.offsetBezier, t),
          ),
          t,
          i,
        ),
      startProportion,
      endProportion,
    );
  }

  resolveToNormalisedScale(size: ReadonlyVec2): CubicBezier {
    return createCubicBezier(
      this.start.resolveToNormalisedScale(vec2.create(), size),
      this.control1.resolveToNormalisedScale(vec2.create(), size),
      this.control2.resolveToNormalisedScale(vec2.create(), size),
      this.end.resolveToNormalisedScale(vec2.create(), size),
    );
  }

  resolveToNormalisedScaleWithTransform(
    size: ReadonlyVec2,
    transform: ReadonlyMat2d,
  ): CubicBezier {
    const normalisedBezier = this.resolveToNormalisedScale(size);
    return transformCubicBezier(normalisedBezier, normalisedBezier, transform);
  }
}

/**
 * Additional options for creating a {@link GraphicSegment}.
 */
export interface GraphicSegmentOptions {
  /**
   * If true, this segment continues seamlessly from the previous segment without
   * starting a new corner. When `false` (default), the segment starts a new corner,
   * which causes dashed stroke patterns to reset. Use this to create continuous dashed
   * strokes across multiple connected segments.
   */
  continuesFromPrevious?: boolean;
}

/**
 * Represents a serializable definition of a {@link GraphicSegment}.
 */
export type GraphicSegmentDefinition =
  | GraphicSubPathStartDefinition
  | GraphicLineDefinition
  | GraphicCubicBezierDefinition;

/**
 * Represents a serializable definition of a {@link GraphicSubPathStart}.
 */
export interface GraphicSubPathStartDefinition {
  type: 'subPathStart';
  start: GraphicPoint;
}

/**
 * Represents a serializable definition of a {@link GraphicLine}.
 */
export interface GraphicLineDefinition {
  type: 'line';
  start: GraphicPoint;
  end: GraphicPoint;
  options?: GraphicSegmentOptions;
}

/**
 * Represents a serializable definition of a {@link GraphicCubicBezier}.
 */
export interface GraphicCubicBezierDefinition {
  type: 'cubicBezier';
  start: GraphicPoint;
  cp1: GraphicPoint;
  cp2: GraphicPoint;
  end: GraphicPoint;
  options?: GraphicSegmentOptions;
}

/**
 * Defines the fill rule for a graphic section.
 */
export type GraphicFillRule = 'nonzero' | 'evenodd';

/**
 * Options for creating a {@link GraphicSection}.
 */
export interface GraphicSectionOptions {
  /**
   * The colour of the fill or stroke. If `undefined`, the colour is inherited from the
   * element drawing the graphic.
   */
  color?: GraphicColor;
  /**
   * The fill rule to use for the section. If not specified, the default is 'nonzero'.
   */
  fillRule?: GraphicFillRule;
}

/**
 * Represents a path that can be rendered in a graphic. It can be a fill or stroke.
 */
export class GraphicSection {
  /**
   * The colour of the fill or stroke. If `undefined`, the colour is inherited from the
   * element drawing the graphic.
   */
  readonly color: GraphicColor | undefined;

  #segments: GraphicSegment[] = [];

  constructor({ color }: GraphicSectionOptions = {}) {
    this.color = color;
  }

  /**
   * Gets the array of segments.
   *
   * You can mutate the vertex data in the graphic.
   * If you do, ensure you call graphics.emitChange() after all mutations are done
   * to notify any subscribers.
   *
   * @returns the array of GraphicSegments
   */
  get segments(): readonly GraphicSegment[] {
    return this.#segments;
  }

  /**
   * Adds a new segment to the fill.
   * @param segment the GraphicsSegment to add
   */
  push(segment: GraphicSegment) {
    this.#segments.push(segment);
  }
}

/**
 * Represents a fill path that can be rendered in a graphic.
 */
export class GraphicFillSection extends GraphicSection {
  /**
   * The fill rule to use for the section.
   */
  readonly fillRule: GraphicFillRule;

  constructor(options: GraphicSectionOptions = {}) {
    super(options);

    this.fillRule = options.fillRule ?? 'nonzero';
  }
}

/**
 * Represents a serializable definition of a {@link GraphicSection}.
 */
export interface GraphicSectionDefinition {
  color?: GraphicColorDefinition;
  segments: GraphicSegmentDefinition[];
}

/**
 * Represents a serializable definition of a {@link GraphicFillSection}.
 */
export interface GraphicFillSectionDefinition extends GraphicSectionDefinition {
  fillRule?: GraphicFillRule;
}

/**
 * Converts a {@link GraphicSegmentDefinition} to a {@link GraphicSegment}.
 *
 * @param definition The segment definition to convert.
 * @returns The corresponding {@link GraphicSegment} instance.
 */
export function segmentDefinitionToSegment(
  definition: GraphicSegmentDefinition,
): GraphicSegment {
  if (definition.type === 'cubicBezier') {
    return new GraphicCubicBezier(
      definition.start,
      definition.cp1,
      definition.cp2,
      definition.end,
      definition.options,
    );
  } else if (definition.type === 'line') {
    return new GraphicLine(definition.start, definition.end, definition.options);
  } else {
    return new GraphicSubPathStart(definition.start);
  }
}
